
import { computed, defineComponent, onMounted, reactive, ref } from 'vue';
import { useDialogWithRouter } from '@/compositions/useDialogWithRouter';
import CustomerCoupon from '@/components/organisms/coupon/CustomerCoupon.vue';
import { useCoupon } from '@/compositions/useCoupon';
import { useShop } from '@/compositions/useShop';
import VueQrcode from 'vue-qrcode';
import { Shop } from '@/repositories/shopRepository';
import DialogSelectCouponAvailableShop from '@/components/organisms/coupon/content/DialogSelectCouponAvailableShop.vue';
import DialogQRCodeReader from '@/components/organisms/DialogQRCodeReader.vue';
import ComsbiSaasScheme from '@comsbi/common/src/libs/comsbi-saas-scheme';
import { Coupon } from '@comsbi/common/src/types/models';
import DialogConfirm from '@/components/molecules/DialogConfirm.vue';
import { useRouter } from 'vue-router/composables';
import { i18n } from '@/plugins/i18n';
import { ToastProgrammatic as Toast } from 'buefy';
import DisplayCurrentTimer from '@/components/atoms/DisplayCurrentTimer.vue';

const CHANNEL_ID =
  process.env.NODE_ENV === 'local' ? window.CHANNEL_ID || process.env.VUE_APP_CHANNEL_ID : window.CHANNEL_ID;

type TabType = 'reader' | 'qr' | 'self';

export default defineComponent({
  name: 'DialogCouponUsing',

  components: {
    DialogQRCodeReader,
    DialogSelectCouponAvailableShop,
    DialogConfirm,
    CustomerCoupon,
    VueQrcode,
    DisplayCurrentTimer,
  },

  props: {
    customerCoupon: {
      type: Object,
      required: true,
    },
  },

  setup(props, { emit }) {
    const router = useRouter();
    const { dialog, open, close, afterClose } = useDialogWithRouter(router);
    const { useCustomerCoupon } = useCoupon();
    const { shopAppFriendly, couponCustomerSettings, getDetail } = useShop();

    const customerCouponRef = ref<InstanceType<typeof CustomerCoupon>>();
    const dialogConfirmRef = ref<InstanceType<typeof DialogConfirm>>();

    const state = reactive<{
      tab: TabType | null;
      shopId?: string;
      agree: boolean;
      isLoading: boolean;
      step: 'CONFIRM' | 'COMPLETE';
      showShopSelector: boolean;
      from: number;
      limit: number;
    }>({
      tab: couponCustomerSettings.value.shopQREnabled
        ? 'reader'
        : couponCustomerSettings.value.shopAppEnabled
        ? 'qr'
        : 'self',
      shopId: undefined,
      agree: false,
      isLoading: false,
      step: 'CONFIRM',
      showShopSelector: false,
      // for pagination
      from: 0,
      limit: 50, // パフォーマンス次第で調整
    });

    const qrCodeValue = computed(() => {
      // クーポン使用QR
      return JSON.stringify({
        type: 'coupon', // 固定 CS-7411
        customerId: props.customerCoupon.customerId,
        customerCouponId: props.customerCoupon.id,
        action: 'use', // 固定
      });
    });

    const endImageUrl = computed(() => {
      try {
        return JSON.parse(props.customerCoupon.couponMaster.attrs).endImageUrl || null;
      } catch {
        return null;
      }
    });

    /**
     * 手動利用可能な状態かどうか（店舗選択済み && 同意済み）
     * CS-4882 クーポン利用設定
     */
    const available = computed(() => {
      if (!couponCustomerSettings.value.manualEnabled) return false;
      if (couponCustomerSettings.value.manualShopSelectable && !state.shopId) return false;
      if (couponCustomerSettings.value.manualAgreementRequired && !state.agree) return false;
      return true;
    });

    /**
     * クーポンを使用（消費）する
     * @param {boolean} isDialog 確認ダイアログを挟むかどうか
     */
    const consumeCoupon = async (isDialog: boolean) => {
      state.isLoading = true;

      const title = `${i18n.t('keyword.coupon')}の使用`;
      const parsedAttrs = JSON.parse(props.customerCoupon.couponMaster.attrs);
      const customerCouponType = parsedAttrs.discount.type;
      let discountMessage = '';

      switch (customerCouponType) {
        case 'CONST': {
          if (parsedAttrs.discount.value > 0) {
            const hasOnlyYen = parsedAttrs.discount.options && parsedAttrs.discount.options.onlyYen;
            if (hasOnlyYen) {
              discountMessage = `\n金額: ${parsedAttrs.discount.value.toLocaleString()}円`;
            } else {
              discountMessage = `\n割引額: ${parsedAttrs.discount.value.toLocaleString()}円`;
            }
          }
          break;
        }
        case 'RATE': {
          discountMessage = `\n割引率: ${parsedAttrs.discount.value}%`;
          break;
        }
        // @deprecated CS-9352
        case 'PRICE': {
          if (parsedAttrs.discount.value > 0) {
            discountMessage = `\n金額: ${parsedAttrs.discount.value.toLocaleString()}円`;
          }
          break;
        }
        default:
          break;
      }

      const isTargetShop =
        !props.customerCoupon.coupon.shopIds 
        || props.customerCoupon.coupon.shopIds.length === 0 
        || props.customerCoupon.coupon.shopIds.includes(state.shopId);

      const message = isTargetShop
        ? `1枚使用します。\nよろしいですか？\n${discountMessage}`
        : '対象外店舗のため、このクーポンは使用できません。';

      const confirmed = isDialog
        ? await dialogConfirmRef.value?.open(title, message, {
            color: 'primary',
            okBtnColor: 'primary',
            width: 320,
            hideCancel: false,
            okBtnDisabled: !isTargetShop,
          })
        : true;

      if (!confirmed) {
        state.isLoading = false;
        return;
      }

      try {
        // クーポン使用
        await useCustomerCoupon(props.customerCoupon.id, state.shopId);

        state.step = 'COMPLETE';
        Toast.open({
          message: '使用しました',
          type: 'is-success',
          position: 'is-top',
        });

        customerCouponRef.value?.stamp(); // 使用済みスタンプを押す
        emit('used'); // 使用完了イベントを送出して、顧客クーポン一覧を再読み込みする
      } catch (e) {
        emit('error', e); // エラーイベントを送出
        Toast.open({
          message: '使用に失敗しました',
          type: 'is-warning',
          position: 'is-top',
        });
        throw e;
      } finally {
        state.isLoading = false;
      }
    };

    const refDialogQRCodeReader = ref<InstanceType<typeof DialogQRCodeReader>>();
    /**
     * クーポン利用店舗判別QR
     * @param value
     */
    const parseQRValue = async (value: string) => {
      if (!value) return;
      try {
        if (value.indexOf('cs:') !== 0) throw new Error('invalid value. value:' + value);
        const [modelKey, modelId, ...args] = ComsbiSaasScheme.parse(value);
        if (modelKey !== ComsbiSaasScheme.SHOP) throw new Error('invalid value. value:' + value);

        // 店舗指定
        state.shopId = modelId;

        // 他LINEアカウントの店舗か判定
        //  => CS-6942 組織クーポンの場合はLINEチャネル関係なく消費させる。チャネルクーポンの場合は店舗を制限する
        if (props.customerCoupon.couponMaster.channelId) {
          const shopChannel = await getDetail(state.shopId);
          const lineChannelId = shopChannel.channel.connection.items[0].toId;
          if (lineChannelId && lineChannelId !== CHANNEL_ID) {
            Toast.open({
              message: `他LINEアカウントの${i18n.t('keyword.coupon')}です`,
              type: 'is-warning',
              position: 'is-top',
            });
            return;
          }
        }

        // 使用
        consumeCoupon(true);
      } catch (e) {
        console.warn(e);
        Toast.open({
          message: 'QRコードの読み取りに失敗しました。',
          type: 'is-warning',
          position: 'is-top',
        });
      }
    };

    const shops = ref<Shop[]>([]);

    onMounted(async () => {
      open();
    });

    return {
      // dialog
      dialog,
      dialogConfirmRef,
      close,
      afterClose,

      shopAppFriendly,
      couponCustomerSettings,
      numOfTabs: computed(() => {
        return [
          couponCustomerSettings.value.shopQREnabled,
          couponCustomerSettings.value.shopAppEnabled,
          couponCustomerSettings.value.manualEnabled,
        ].filter(v => v).length;
      }),

      customerCouponRef,
      state,
      qrCodeValue,
      endImageUrl,
      available,
      shops,
      consumeCoupon,

      // CS-4882
      refDialogQRCodeReader,
      parseQRValue,

      // CS-6329
      filterShopIds: computed(() => {
        const coupon: Coupon = props.customerCoupon.coupon;
        return coupon.shopIds || [];
      }),
    };
  },
});
